if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        this.initR3nuGalleryDots();
        this.initAutoplay();
        this.initR3nuVideoLightbox();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initR3nuThumbnailColumnToMain();
      }

      disconnectedCallback() {
        if (this._r3nuThumbRo) {
          this._r3nuThumbRo.disconnect();
          this._r3nuThumbRo = null;
        }
        if (this._r3nuThumbMqlCb && this.mql) {
          this.mql.removeEventListener('change', this._r3nuThumbMqlCb);
          this._r3nuThumbMqlCb = null;
        }
        this.style?.removeProperty?.('--r3nu-gallery-main-height');
      }

      /**
       * R3NU thumbnail carousel: cap the left column to the main viewer height so the 3:4 hero
       * is never stretched; thumbs scroll inside the column (see section-main-product-r3nu.css).
       */
      initR3nuThumbnailColumnToMain() {
        if (this.dataset.desktopLayout !== 'thumbnail_slider' || !this.elements.thumbnails || !this.elements.viewer) {
          return;
        }

        const apply = () => {
          if (!this.mql.matches) {
            this.style.removeProperty('--r3nu-gallery-main-height');
            return;
          }
          const h = this.elements.viewer.offsetHeight;
          if (h > 0) {
            this.style.setProperty('--r3nu-gallery-main-height', `${Math.round(h)}px`);
          }
        };

        this._r3nuThumbRo = new ResizeObserver(() => {
          window.requestAnimationFrame(apply);
        });
        this._r3nuThumbRo.observe(this.elements.viewer);
        this._r3nuThumbMqlCb = () => apply();
        this.mql.addEventListener('change', this._r3nuThumbMqlCb);
        window.requestAnimationFrame(apply);
      }

      /*
       * An image opens the lightbox through its own modal-opener button, but
       * R3NU renders video as an inline autoplaying <video> alongside a
       * modal-opener that CSS hides on desktop — so clicking the video did
       * nothing. Forward those clicks to the hidden opener's button, which is
       * what ModalOpener listens on and what carries the data-media-id the
       * modal reads. The inline player has no controls of its own, so nothing
       * is lost by claiming the click.
       */
      initR3nuVideoLightbox() {
        this.querySelectorAll('.product__media-item').forEach((item) => {
          const player = item.querySelector('.product__media-video');
          const toggle = item.querySelector('modal-opener .product__media-toggle');
          if (!player || !toggle || player.dataset.r3nuLightbox) return;

          player.dataset.r3nuLightbox = 'true';
          player.setAttribute('role', 'button');
          player.setAttribute('tabindex', '0');
          // The toggle's accessible name sits in a .visually-hidden span, not aria-label.
          const label = toggle.getAttribute('aria-label') || toggle.textContent.trim();
          if (label) player.setAttribute('aria-label', label);
          player.setAttribute('aria-haspopup', 'dialog');

          player.addEventListener('click', () => toggle.click());
          player.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggle.click();
          });
        });
      }

      initAutoplay() {
        const interval = parseInt(this.dataset.autoplayInterval, 10);
        if (!interval || interval <= 0) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this._apInterval = interval * 1000;
        this._apPaused = false;
        this._apTimer = null;
        this._apVideo = null;
        this._apVideoHandler = null;

        this.addEventListener('mouseenter', () => this._pauseAutoplay());
        this.addEventListener('mouseleave', () => this._resumeAutoplay());
        this.addEventListener('focusin', () => this._pauseAutoplay());
        this.addEventListener('focusout', () => this._resumeAutoplay());

        this._scheduleNext();
      }

      _getSlides() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
      }

      _getActiveSlide() {
        return this.elements.viewer.querySelector('li[data-media-id].is-active') || this._getSlides()[0];
      }

      _scheduleNext() {
        this._clearAutoplay();
        if (this._apPaused || this._apInterval == null) return;

        const activeSlide = this._getActiveSlide();
        const video = activeSlide?.querySelector('.product__media-video video');

        if (video && !video.loop) {
          this._apVideo = video;
          this._apVideoHandler = () => {
            this._apVideo = null;
            this._apVideoHandler = null;
            if (!this._apPaused) this._advanceSlide();
          };
          video.addEventListener('ended', this._apVideoHandler, { once: true });
        } else {
          this._apTimer = setTimeout(() => this._advanceSlide(), this._apInterval);
        }
      }

      _advanceSlide() {
        const slides = this._getSlides();
        if (slides.length < 2) return;
        const active = this._getActiveSlide();
        const idx = slides.indexOf(active);
        const next = slides[(idx + 1) % slides.length];
        this.setActiveMedia(next.dataset.mediaId, false);
      }

      _clearAutoplay() {
        if (this._apTimer) { clearTimeout(this._apTimer); this._apTimer = null; }
        if (this._apVideo && this._apVideoHandler) {
          this._apVideo.removeEventListener('ended', this._apVideoHandler);
          this._apVideo = null;
          this._apVideoHandler = null;
        }
      }

      _pauseAutoplay() {
        this._apPaused = true;
        this._clearAutoplay();
      }

      _resumeAutoplay() {
        this._apPaused = false;
        this._scheduleNext();
      }

      /**
       * R3NU pagination dots (mobile): no click wiring in Dawn — sync on swipe + jump slide on tap.
       */
      initR3nuGalleryDots() {
        const dotsRoot = this.querySelector('[data-r3nu-gallery-dots]');
        if (!dotsRoot || !this.elements.viewer) return;

        dotsRoot.querySelectorAll('.product-r3nu-gallery-dots__dot').forEach((dot) => {
          const go = () => {
            const idx = Number(dot.dataset.r3nuDotIndex);
            if (Number.isNaN(idx)) return;
            const slides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
            const targetSlide = slides[idx];
            const mediaId = targetSlide?.getAttribute('data-media-id');
            if (mediaId) this.setActiveMedia(mediaId, false);
          };
          dot.addEventListener('click', go);
          dot.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              go();
            }
          });
          dot.setAttribute('role', 'button');
          dot.setAttribute('tabindex', '0');
        });
      }

      onSlideChanged(event) {
        const current = event.detail?.currentElement;
        if (!current) return;

        this.updateR3nuDots(current);
        if (this._apInterval != null) this._scheduleNext();

        if (!this.elements.thumbnails) return;

        const mediaId = current.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: scrollBehavior });
          }
          // Intentionally no window.scrollTo: Dawn’s “scroll main image into view” caused the page to
          // jump to the top (or y≈0) on many slide/dot/thumbnail changes when users had scrolled down.
        });
        this.playActiveMedia(activeMedia);
        this.updateR3nuDots(activeMedia);
        if (this._apInterval != null) this._scheduleNext();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail.dataset?.mediaPosition;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      updateR3nuDots(activeSlide) {
        const dotsRoot = this.querySelector('[data-r3nu-gallery-dots]');
        if (!dotsRoot || !activeSlide || !this.elements.viewer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const idx = slides.indexOf(activeSlide);
        if (idx < 0) return;
        dotsRoot.querySelectorAll('.product-r3nu-gallery-dots__dot').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === idx);
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const thumbTrack = this.elements.thumbnails.slider;
        const verticalThumbs = thumbTrack?.classList.contains('slider--vertical');
        if (verticalThumbs) {
          thumbTrack.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          thumbTrack.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        const autoplayVideo = activeItem.querySelector('.product__media-video video');
        if (autoplayVideo) {
          autoplayVideo.currentTime = 0;
          autoplayVideo.play().catch(() => {});
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
